// A chat's investigation drawn as a graph on one clock: the machines it
// reached and their sessions as a tree of nodes down the left, the people seen
// on each machine beside it, what happened in each session as marks in its
// lane (the new ones labelled where the label fits), the windows it queried as
// bands; each claim a numbered badge over the lanes with stems down to the
// marks it rests on, each host reached a node under them with stems up to the
// processes that reached it. graphTimelineLayout places everything; this
// draws it. A step control shows the picture after any of the chat's steps,
// with that step's additions in the graph colour. A hovered mark, lane, badge,
// host, window, break or step says what it is in a tooltip at it, and
// hovering a claim lights what it rests on. With `at` it draws one step with
// no step control: the transcript's picture after a call.
import React, { useEffect, useMemo, useRef, useState } from "react";

import HoverTooltip from "./HoverTooltip.jsx";
import { claimFocus, graphSteps, timelineView } from "../lib/graphTimeline.js";
import { LAYOUT, clip, gapText, layoutTimeline, namedText } from "../lib/graphTimelineLayout.js";
import { claimDisplayRows } from "../lib/investigationGraphView.js";

const MAX_CLAIMS = 12;
// Rail labels are sans (a machine's at 11px, a session's at 10.5px, its id
// at 9.5px); their widths are estimated from length, as the layout's are.
const MACHINE_CHAR_W = 6.6;
const SESSION_CHAR_W = 6.0;
const ID_W = 48;

const CLOCK = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const CLOCK_S = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
const DAY_CLOCK = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const HOURS_20 = 20 * 3600_000;

const plural = (n, word) => `${n} ${n === 1 ? word : word === "person" ? "people" : `${word}${word.endsWith("s") ? "es" : "s"}`}`;

function tickText(t, spanMs) {
    if (spanMs < HOURS_20) return CLOCK.format(t);
    const d = new Date(t);
    return d.getHours() === 0 && d.getMinutes() === 0 ? DAY.format(t) : DAY_CLOCK.format(t);
}

// The days the clock covers, since its ticks carry only clock times.
function axisDay(scale) {
    if (!scale) return "no times yet";
    const a = DAY.format(scale.first);
    const b = DAY.format(scale.last);
    return a === b ? a : `${a} - ${b}`;
}

// A window's range, its date said once when it starts and ends on one day.
function rangeText(from, to) {
    const sameDay = DAY.format(from) === DAY.format(to);
    return `${DAY_CLOCK.format(from)} to ${sameDay ? CLOCK.format(to) : DAY_CLOCK.format(to)}`;
}

function timeText(t, spanMs) {
    return spanMs >= HOURS_20 ? DAY_CLOCK.format(t) : CLOCK_S.format(t);
}

// `ready` re-runs the measure once the measured element is rendered.
function useWidth(ref, ready) {
    const [width, setWidth] = useState(600);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        if (el.clientWidth) setWidth(el.clientWidth);
        if (typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(() => { if (el.clientWidth) setWidth(el.clientWidth); });
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref, ready]);
    return width;
}

export function Glyph({ kind, x, y, fill, opacity = 1 }) {
    if (kind === "message") return <circle cx={x} cy={y} r={2.4} fill={fill} opacity={opacity} />;
    if (kind === "prompt") return <rect x={x - 2.1} y={y - 2.1} width={4.2} height={4.2} transform={`rotate(45 ${x} ${y})`} fill={fill} opacity={opacity} />;
    return <rect x={x - 2.2} y={y - 2.2} width={4.4} height={4.4} fill={fill} opacity={opacity} />;
}

function bucketText(b, spanMs, where, newWords) {
    const first = b.events[0];
    const fresh = b.isNew ? `, ${newWords}` : "";
    if (b.events.length === 1) {
        const reached = first.reached?.length ? `, reached ${first.reached.slice(0, 3).join(", ")}` : "";
        return `${first.kind} at ${timeText(first.at, spanMs)} in ${where}: ${first.label}${reached}${fresh}`;
    }
    const names = b.events.slice(0, 2).map((e) => e.label).join("; ");
    const more = b.events.length > 2 ? `; +${b.events.length - 2} more` : "";
    return `${plural(b.events.length, b.kind)} around ${timeText(first.at, spanMs)} in ${where}: ${names}${more}${fresh}`;
}

const rowName = (row) => [row.label, row.word && row.word !== row.label ? row.word : null, row.detail].filter(Boolean).join(" ");

// Why a session with nothing in it is on the chart at all: the results that
// named it, and that none of its events are in the graph.
const namedWhy = (named) => `${namedText(named)}, none of its processes, messages or prompts in the graph yet`;

function rowText(row, spanMs) {
    const seen = row.span ? `seen ${timeText(row.span.from, spanMs)} to ${timeText(row.span.to, spanMs)}` : null;
    const events = row.events.length ? plural(row.events.length, "event") : namedWhy(row.named);
    const undated = row.undated ? `${row.undated} with no time` : null;
    return [rowName(row), seen, events, undated].filter(Boolean).join(", ");
}

// What a step found, by kind: "process events: 28 processes, 1 window".
export function stepText(s) {
    if (s.claims.length && !s.anchors && !s.relations) return s.label;
    const kinds = Object.entries(s.kinds).sort((a, b) => b[1] - a[1]).map(([kind, n]) => plural(n, kind));
    const parts = [...kinds.slice(0, 3), kinds.length > 3 ? "more" : null, !s.anchors && s.relations ? `${plural(s.relations, "relation")} between known anchors` : null, s.claims.length ? plural(s.claims.length, "claim") : null].filter(Boolean);
    return `${s.label}: ${parts.join(", ") || "nothing new"}`;
}

// What a call that ran other calls added across their steps, by kind.
function callText(steps, ids) {
    const kinds = {};
    let claims = 0;
    for (const s of steps) {
        if (!ids.has(s.id)) continue;
        for (const [kind, n] of Object.entries(s.kinds)) kinds[kind] = (kinds[kind] || 0) + n;
        claims += s.claims.length;
    }
    const parts = Object.entries(kinds).sort((a, b) => b[1] - a[1]).map(([kind, n]) => plural(n, kind));
    if (claims) parts.push(plural(claims, "claim"));
    return parts.join(", ") || "nothing new";
}

function Chevron({ back }) {
    return (
        <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={back ? "M6.5 2 3.5 5l3 3" : "M3.5 2l3 3-3 3"} />
        </svg>
    );
}

export { Chevron as StepChevron };

// The step control is the board's own (SessionCanvasView's BoardSteps): one
// line of what the step found, the step and its neighbours, every step as a
// segment (the one shown in the graph colour), and whether it follows the
// chat, so the graph reads as part of the workspace it is opened from.
const STEP_BUTTON = "grid h-[16px] w-[16px] place-items-center rounded-[2px] bg-transparent text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-40 cursor-pointer";

function StepControl({ steps, step, following, onPick, tipFor }) {
    const cur = steps[step];
    return (
        <div className="mb-[8px] flex h-[16px] items-center gap-[6px] text-[10.5px]" data-graph-steps>
            <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]" {...tipFor(() => `step ${step + 1}, ${stepText(cur)}`)}>{stepText(cur)}</span>
            <button type="button" aria-label="Previous step" disabled={step <= 0} onClick={() => onPick(step - 1)} className={STEP_BUTTON}><Chevron back /></button>
            <span className="shrink-0 tabular-nums text-[color:var(--ink)]" data-graph-step-label>step {step + 1} of {steps.length}</span>
            <button type="button" aria-label="Next step" disabled={step >= steps.length - 1} onClick={() => onPick(step + 1)} className={STEP_BUTTON}><Chevron /></button>
            <div
                className="flex h-[6px] w-[96px] shrink-0 gap-[1px] outline-none"
                role="group"
                aria-label="Steps"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "ArrowLeft" && step > 0) { e.preventDefault(); onPick(step - 1); }
                    if (e.key === "ArrowRight" && step < steps.length - 1) { e.preventDefault(); onPick(step + 1); }
                }}
            >
                {steps.map((s, i) => (
                    <button
                        key={s.id}
                        type="button"
                        aria-label={`Step ${i + 1}: ${stepText(s)}`}
                        aria-current={i === step ? "step" : undefined}
                        data-graph-step={i}
                        onClick={() => onPick(i)}
                        {...tipFor(() => `step ${i + 1}, ${stepText(s)}`)}
                        style={{ flex: "1 1 0", minWidth: 2 }}
                        className={[
                            "h-full cursor-pointer border-0 p-0",
                            i === step ? "bg-[color:var(--graph)]" : i < step ? "bg-[color:var(--line-strong)]" : "bg-[color:var(--line-soft)]",
                        ].join(" ")}
                    />
                ))}
            </div>
            {following ? (
                <span className="shrink-0 text-[color:var(--muted-soft)]">following the chat</span>
            ) : (
                <button type="button" onClick={() => onPick(null)} className="shrink-0 cursor-pointer bg-transparent text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]">
                    latest
                </button>
            )}
        </div>
    );
}

function Legend({ newWords, links }) {
    const item = (glyph, text) => (
        <span className="inline-flex items-center gap-[3px]">
            <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true">{glyph}</svg>
            {text}
        </span>
    );
    return (
        <div className="mt-[4px] flex flex-wrap items-center gap-[10px] text-[10px] text-[color:var(--muted)]">
            {item(<Glyph kind="process" x={7} y={5} fill="var(--muted)" />, "process")}
            {item(<Glyph kind="message" x={7} y={5} fill="var(--muted)" />, "message")}
            {item(<Glyph kind="prompt" x={7} y={5} fill="var(--muted)" />, "prompt")}
            {item(<rect x="2" y="1" width="10" height="8" fill="color-mix(in srgb, var(--graph) 10%, transparent)" />, "queried window")}
            {item(<rect x="4.5" y="2.5" width="5" height="5" fill="var(--graph)" />, newWords)}
            {links ? item(<path d="M1 5 H13" stroke="var(--graph)" strokeWidth="1.2" />, "a claim rests on") : null}
            {links ? item(<path d="M1 5 H13" stroke="var(--muted)" strokeWidth="1.2" />, "a host was reached") : null}
        </div>
    );
}

// The claims as the board's claim chips say them: the board's badge (filled
// when the graph bears the claim out, outlined while it does not yet), the
// statement in the panel's own type, and whether it is supported in the
// board's words and colours.
function ClaimBadge({ n, supported }) {
    return (
        <span className={`mt-[2px] grid h-[12px] min-w-[14px] shrink-0 place-items-center border border-[color:var(--graph)] px-[2px] text-[9.5px] font-semibold leading-none ${supported ? "bg-[color:var(--graph)] text-[color:var(--bg)]" : "text-[color:var(--graph)]"}`}>{n}</span>
    );
}

function ClaimRows({ claims, numbers, focusId, pinnedId, claimTrace, onOpenTrace, onFocus, onPin }) {
    if (!claims.length) return null;
    return (
        <div className="mt-[10px] border-t border-[color:var(--line-soft)] pt-[8px]" data-graph-claims onMouseLeave={() => onFocus(null)}>
            <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">claims</div>
            {claimDisplayRows(claims).slice(0, MAX_CLAIMS).map((c) => {
                const traceId = claimTrace?.(c.id) || null;
                const supported = String(c.status || "").startsWith("supported");
                const bearing = String(c.status || "").includes("context") ? "context" : null;
                return (
                    <div
                        key={c.id}
                        data-graph-claim={c.id}
                        onMouseEnter={() => onFocus(c.id)}
                        className={[
                            "-mx-[4px] flex flex-wrap items-start gap-x-[8px] gap-y-[2px] rounded-[2px] border-b border-[color:var(--line-soft)] px-[4px] py-[5px] last:border-b-0",
                            c.id === focusId ? "bg-[color:var(--gray-fill)]" : "",
                        ].join(" ")}
                    >
                        {numbers?.has(c.id) ? <ClaimBadge n={numbers.get(c.id)} supported={supported} /> : null}
                        <button
                            type="button"
                            aria-pressed={c.id === pinnedId}
                            onClick={() => onPin(c.id === pinnedId ? null : c.id)}
                            className="min-w-[200px] flex-1 cursor-pointer whitespace-normal break-words bg-transparent p-0 text-left text-[12px] leading-[1.45] text-[color:var(--ink)]"
                        >
                            {c.isNew ? <span className="mr-[5px] text-[10.5px] text-[color:var(--graph)]">new</span> : null}
                            {c.statement}
                        </button>
                        <span className="flex shrink-0 items-baseline gap-[6px] pt-[1px] text-[10.5px] leading-[1.45]">
                            {/* A reader's context claim says its bearing beside the
                                status, as the chat's reader card does. */}
                            <span>
                                <span className={supported ? "text-[color:var(--green)]" : "text-[color:var(--severity-medium)]"}>
                                    {supported ? "supported" : "not yet supported"}
                                </span>
                                {bearing ? <span className="text-[color:var(--muted)]">{` \u00b7 ${bearing}`}</span> : null}
                            </span>
                            <span className="text-[color:var(--muted-soft)]">{c.kind}</span>
                            {traceId ? (
                                <button type="button" data-graph-claim-canvas={c.id} onClick={() => onOpenTrace(traceId, c.id)} className="cursor-pointer bg-transparent p-0 text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                                    workspace
                                </button>
                            ) : null}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// A node's stems: down (or up) from the node to a bar, along the bar, and to
// each thing it joins, ending on a dot there.
function combPath(c) {
    const parts = [`M ${c.node.x} ${c.node.y} V ${c.bar}`, `M ${c.x0} ${c.bar} H ${c.x1}`];
    for (const t of c.stems) parts.push(`M ${t.x} ${c.bar} V ${t.y}`);
    return parts.join(" ");
}

function GraphChart({ L, view, focus, focusNode, newWords, spanMs, tipFor, onFocusNode }) {
    const top = L.lanesTop;
    const marksByLane = useMemo(() => {
        const m = new Map();
        for (const b of L.marks) {
            if (!m.has(b.lane)) m.set(b.lane, []);
            m.get(b.lane).push(b);
        }
        return m;
    }, [L]);
    const machineChars = Math.max(8, Math.floor((L.rail - 20) / MACHINE_CHAR_W));
    const sessionChars = (detail) => Math.max(4, Math.floor((L.rail - 34 - (detail ? ID_W : 0)) / SESSION_CHAR_W));
    const lit = (key) => !focus || focus.rows.has(key) || focus.groups.has(key);
    const combOpacity = (c) => (focusNode ? (c.from === focusNode ? 1 : 0.12) : c.kind === "cites" ? 0.75 : 0.6);
    const hover = (key, text) => ({
        onMouseEnter: (e) => { onFocusNode(key); tipFor(text).onMouseEnter(e); },
        onMouseLeave: (e) => { onFocusNode(null); tipFor(text).onMouseLeave(e); },
    });
    return (
        <svg width={L.width} height={L.height} className="block overflow-visible" data-graph-chart role="img"
            aria-label={`${plural(view.groups.length, "machine")} with ${plural(view.groups.reduce((n, g) => n + g.rows.length, 0), "lane")} of events`}>
            {/* Behind everything: machine separators, the clock's gridlines, queried windows, breaks. */}
            {L.lanes.filter((l) => l.type === "machine").map((l) => (
                <rect key={`sep:${l.key}`} x={0} y={l.y} width={L.width} height={1} fill="var(--line-soft)" />
            ))}
            {L.scale ? L.scale.ticks.map((t) => (
                <rect key={`grid:${t.t}`} x={Math.round(t.x)} y={top} width={1} height={L.lanesBottom - top} fill="var(--line-soft)" />
            )) : null}
            {L.windows.map((w) => {
                const strong = w.isNew || focus?.windows.has(w.key);
                return (
                    <g key={`band:${w.key}`}>
                        <rect x={w.x0} y={top} width={Math.max(1, w.x1 - w.x0)} height={L.lanesBottom - top} fill={`color-mix(in srgb, var(--graph) ${strong ? 8 : 4}%, transparent)`} />
                        {w.edgeLeft ? <rect x={w.x0} y={top} width={1} height={L.lanesBottom - top} fill="color-mix(in srgb, var(--graph) 35%, transparent)" /> : null}
                        {w.edgeRight ? <rect x={w.x1 - 1} y={top} width={1} height={L.lanesBottom - top} fill="color-mix(in srgb, var(--graph) 35%, transparent)" /> : null}
                    </g>
                );
            })}
            {L.scale ? L.scale.breaks.map((b) => (
                <g key={`break:${b.x}`} data-graph-break>
                    <rect x={b.x0 + 3} y={top} width={Math.max(1, b.x1 - b.x0 - 6)} height={L.lanesBottom - top} fill="var(--timeline-surface,var(--bg))" opacity={0.7} />
                    {/* The break's mark and its hover sit in the strip just over the lanes,
                        under the claims' badges, so a badge's hover is never the break's. */}
                    <path d={`M ${b.x - 3} ${top - 1} l 2 -5 M ${b.x + 1} ${top - 1} l 2 -5`} stroke="var(--muted-soft)" strokeWidth={1} fill="none" />
                    <rect x={b.x0} y={top - 6} width={b.x1 - b.x0} height={L.lanesBottom - top + 6} fill="transparent" {...tipFor(`${gapText(b.ms)} with nothing on the chart, left out`)} />
                </g>
            )) : null}

            {/* The rail: machines, and their sessions as a tree. */}
            {L.tree.map((t) => (
                <path key={`tree:${t.y0}`} d={`M ${t.x} ${t.y0} V ${t.y1} ${t.elbows.map((y) => `M ${t.x} ${y} H ${t.x + 8}`).join(" ")}`} stroke="var(--line-strong)" strokeWidth={1} fill="none" />
            ))}
            {L.spans.map((sp) => (
                <line key={`span:${sp.key}`} x1={sp.x0} x2={Math.max(sp.x0 + 1, sp.x1)} y1={sp.y} y2={sp.y} stroke={sp.isNew ? "var(--graph)" : "var(--line-strong)"} strokeWidth={1} opacity={focus && !lit(sp.key) ? 0.4 : 1} />
            ))}
            {L.lanes.map((lane) => {
                if (lane.type === "machine") {
                    const n = L.machineNodes.get(lane.key);
                    const on = focus?.groups.has(lane.key);
                    const g = lane.group;
                    const sessions = (g.rows || []).filter((r) => r.kind === "session").length;
                    const people = g.people?.length ? `, people seen: ${g.people.map((p) => p.label).join(", ")}` : "";
                    const text = lane.bare ? `${g.label}: ${namedText(g.named)}, nothing from it in the graph yet${people}` : `${g.label}: ${plural(sessions, "session")}, ${plural(g.count || 0, "event")}${people}`;
                    return (
                        <g key={`machine:${lane.key}`} data-graph-machine={lane.key} data-bare={lane.bare ? "" : undefined} opacity={focus && !on && !(g.rows || []).some((r) => focus.rows.has(r.key)) ? 0.45 : 1}>
                            <rect x={n.x - 3.5} y={n.y - 3.5} width={7} height={7} fill={lane.bare ? "none" : lane.isNew ? "var(--graph)" : "var(--ink)"} stroke={lane.bare ? "var(--muted)" : "none"} strokeWidth={1.2} />
                            <text x={16} y={lane.y + 14} fontSize="11" fontWeight={500} fill={on ? "var(--graph)" : lane.bare ? "var(--muted)" : "var(--ink)"}>{clip(g.label, machineChars)}</text>
                            {lane.bare && !(g.people || []).length ? <text x={L.plot.x0 + 4} y={lane.y + 14} fontSize="9.5" fill="var(--muted-soft)">{namedText(g.named)}</text> : null}
                            <rect x={0} y={lane.y} width={L.rail} height={lane.h} fill="transparent" {...tipFor(text)} />
                        </g>
                    );
                }
                if (lane.type === "more") {
                    return <text key={lane.key} x={16} y={lane.y + 10} fontSize="9.5" fill="var(--muted-soft)">{lane.label}</text>;
                }
                const row = lane.row;
                const n = L.laneNodes.get(lane.key);
                // The name before the id: a name that does not fit beside its id
                // drops the id (the tooltip keeps it) rather than being cut.
                const withId = Boolean(row.detail) && String(row.label).length <= sessionChars(true);
                const on = focus?.rows.has(lane.key);
                const session = lane.type === "session";
                const where = session ? `${rowName(row)} on ${lane.group.label}` : `${lane.group.label}, no session known`;
                return (
                    <g key={`${lane.type}:${lane.key}`} data-graph-row={lane.key}>
                        {session
                            ? <circle cx={n.x} cy={n.y} r={3.5} fill={row.isNew ? "var(--graph)" : "var(--muted)"} />
                            : <circle cx={n.x} cy={n.y} r={3} fill="none" stroke="var(--muted)" strokeWidth={1.2} />}
                        <text x={28} y={n.y + 3.5} fontSize="10.5" fill={on ? "var(--graph)" : lit(lane.key) ? "var(--ink)" : "var(--muted-soft)"}>
                            {withId ? row.label : clip(row.label, sessionChars(false))}
                            {withId ? <tspan fontSize="9.5" fill="var(--muted-soft)" dx={5}>{row.detail}</tspan> : null}
                        </text>
                        <rect x={0} y={lane.y} width={L.rail} height={lane.h} fill="transparent" {...tipFor(() => rowText(row, spanMs))} />
                        {(marksByLane.get(lane.key) || []).map((b) => {
                            const cited = b.events.some((e) => focus?.events.has(e.key));
                            const fill = cited || (b.isNew && !focus) ? "var(--graph)" : "var(--muted)";
                            return (
                                <g key={b.key} data-timeline-mark={b.kind} data-new={b.isNew ? "" : undefined} data-cited={cited ? "" : undefined}>
                                    {cited ? <circle cx={b.x} cy={b.y} r={4.6} fill="none" stroke="var(--graph)" strokeWidth={1} /> : null}
                                    <Glyph kind={b.kind} x={b.x} y={b.y} fill={fill} opacity={focus && !cited ? 0.25 : 1} />
                                    <rect x={b.x - 3} y={b.y - 2.5} width={6} height={5} fill="transparent" {...tipFor(() => bucketText(b, spanMs, where, newWords))} />
                                </g>
                            );
                        })}
                        {row.undated && L.hasUndated ? (
                            <text x={L.plot.x1 + LAYOUT.undatedW - 4} y={n.y + 3} textAnchor="end" fontSize="9.5" className="tabular-nums" fill="var(--muted-soft)">+{row.undated}</text>
                        ) : null}
                    </g>
                );
            })}
            {L.chips.map((c) => c.more ? (
                <text key={c.key} x={c.x} y={c.y + 10} fontSize="9.5" fill="var(--muted-soft)">+{c.more}</text>
            ) : (
                <g key={`chip:${c.lane}:${c.key}`} data-graph-chip={c.key} {...tipFor(`${c.full}, seen on this machine`)}>
                    <rect x={c.x} y={c.y} width={c.w} height={LAYOUT.chipH} fill="var(--timeline-surface,var(--bg))" stroke={focus && (focus.groups.has(c.lane)) && focusNode === c.key ? "var(--graph)" : "var(--line)"} strokeWidth={1} />
                    <text x={c.x + 4} y={c.y + 9.5} fontSize="9.5" fill={c.isNew ? "var(--graph)" : "var(--muted)"}>{c.label}</text>
                </g>
            ))}
            {L.labels.map((lab) => {
                const b = L.marks.find((m) => m.key === lab.key);
                const cited = b && b.events.some((e) => focus?.events.has(e.key));
                return (
                    <text key={`label:${lab.key}`} data-graph-label={lab.key} x={lab.x} y={lab.y} textAnchor={lab.anchor} fontSize="9.5" fill={cited ? "var(--graph)" : "var(--ink)"} opacity={focus && !cited ? 0.3 : 0.85}>
                        {lab.text}
                    </text>
                );
            })}
            {L.notes.map((note) => (
                <text key={`note:${note.key}`} x={note.x} y={note.y} textAnchor={note.anchor} fontSize="9.5" fill="var(--muted-soft)">{note.text}</text>
            ))}

            {/* Stems from the claims' badges down, and from the hosts up. */}
            <g pointerEvents="none">
                {L.combs.map((c) => (
                    <g key={`comb:${c.kind}:${c.from}:${c.lane || ""}`} data-graph-comb={c.kind} opacity={combOpacity(c)}>
                        <path d={combPath(c)} fill="none" stroke={c.kind === "cites" ? "var(--graph)" : "var(--muted)"} strokeWidth={focusNode === c.from ? 1.4 : 1} />
                        {c.stems.map((t) => <circle key={t.key} cx={t.x} cy={t.y} r={1.8} fill={c.kind === "cites" ? "var(--graph)" : "var(--muted)"} />)}
                    </g>
                ))}
            </g>
            {L.badges.map((b) => (
                <g key={b.key} data-graph-badge={b.claimId} data-new={b.isNew ? "" : undefined} {...hover(b.key, `claim ${b.n}: ${b.statement}`)}>
                    <rect
                        x={b.x - LAYOUT.badgeW / 2}
                        y={b.y - LAYOUT.badgeH / 2}
                        width={LAYOUT.badgeW}
                        height={LAYOUT.badgeH}
                        fill={b.status === "insufficient" ? "var(--timeline-surface,var(--bg))" : "var(--graph)"}
                        stroke="var(--graph)"
                        strokeWidth={focusNode === b.key ? 2 : 1}
                    />
                    <text x={b.x} y={b.y + 3.5} textAnchor="middle" fontSize="9.5" fontWeight={600} fill={b.status === "insufficient" ? "var(--graph)" : "var(--timeline-surface,var(--bg))"}>{b.n}</text>
                </g>
            ))}
            {L.hosts.map((h) => (
                <g key={`${h.lane}:${h.key}`} data-graph-host={h.key} data-new={h.isNew ? "" : undefined} {...hover(h.key, `${h.full}: reached by ${plural(h.from.reduce((n, b) => n + b.events.length, 0), "process")} in this lane`)}>
                    <rect x={h.x - 3} y={h.y - 3} width={6} height={6} transform={`rotate(45 ${h.x} ${h.y})`} fill="var(--timeline-surface,var(--bg))" stroke={h.isNew ? "var(--graph)" : "var(--muted)"} strokeWidth={1.2} />
                    <text x={h.x + 7} y={h.y + 3.5} fontSize="9.5" fill={focusNode === h.key ? "var(--ink)" : "var(--muted)"}>{h.label}</text>
                </g>
            ))}

            {/* The clock, over everything. */}
            <text x={0} y={12} fontSize="10" fill="var(--muted)">{axisDay(L.scale)}</text>
            {L.scale ? L.scale.ticks.map((t, i) => (
                // A label that would run into the "no time" column's is left
                // to its gridline.
                L.hasUndated && L.plot.x1 - t.x < 48 ? null : (
                    <text key={`tick:${t.t}`} x={t.x} y={12} fontSize="9.5" fill="var(--muted-soft)" textAnchor={i === 0 && t.x - L.plot.x0 < 24 ? "start" : L.plot.x1 - t.x < 24 ? "end" : "middle"}>
                        {tickText(t.t, L.scale.last - L.scale.first)}
                    </text>
                )
            )) : null}
            {L.hasUndated ? <text x={L.plot.x1 + LAYOUT.undatedW - 4} y={12} fontSize="9.5" textAnchor="end" fill="var(--muted)">no time</text> : null}
            {L.windows.map((w) => (
                <rect
                    key={`bar:${w.key}`}
                    data-graph-window={w.key}
                    x={w.x0}
                    y={LAYOUT.axisH - 3}
                    width={Math.max(2, w.x1 - w.x0)}
                    height={3}
                    fill={w.isNew || focus?.windows.has(w.key) ? "var(--graph)" : "color-mix(in srgb, var(--graph) 45%, transparent)"}
                    {...tipFor(() => `queried ${rangeText(w.from, w.to)}${w.isNew ? `, ${newWords}` : ""}`)}
                />
            ))}
        </svg>
    );
}

export default function GraphTimeline({ graph, version = 0, at = null, claimTrace = null, onOpenTrace = () => {} }) {
    const steps = useMemo(() => graphSteps(graph), [graph, version]);
    // null follows the chat: each new step becomes the one shown.
    const [picked, setPicked] = useState(null);
    const last = steps.length - 1;
    const fixed = at != null;
    const step = fixed ? Math.min(last, Math.max(0, at.step)) : picked == null || picked >= last ? last : Math.max(0, picked);
    const following = !fixed && (picked == null || picked >= last);
    const fresh = at?.fresh || null;
    const freshSteps = at?.freshSteps || null;
    const view = useMemo(() => timelineView(graph, { step, fresh, freshSteps }), [graph, version, step, fresh, freshSteps]);
    const first = fixed && Number.isFinite(at.first) ? Math.max(0, Math.min(step, at.first)) : step;
    const newWords = fixed ? "added by this call" : "new at this step";
    // The hovered thing's rect and what it says, for the one tooltip.
    const [tip, setTip] = useState(null);
    const tipFor = (label) => ({
        onMouseEnter: (e) => setTip({ rect: e.currentTarget.getBoundingClientRect(), label: typeof label === "function" ? label() : label }),
        onMouseLeave: () => setTip(null),
    });
    const [claimHover, setClaimHover] = useState(null);
    const [claimPin, setClaimPin] = useState(null);
    // A host (or a person) hovered is picked out as a claim's evidence is: a
    // host by the marks that reached it.
    const [nodeFocus, setNodeFocus] = useState(null);
    const focusId = claimHover ?? claimPin;
    const focusClaim = focusId ? view.claims.find((c) => c.id === focusId) || null : null;
    const focusCites = useMemo(() => {
        if (focusClaim) return focusClaim.evidence;
        if (!nodeFocus) return null;
        const reachedBy = view.groups.flatMap((g) => g.rows.flatMap((r) => r.events.filter((e) => e.reachedKeys?.includes(nodeFocus)).map((e) => e.key)));
        return [nodeFocus, ...reachedBy];
    }, [focusClaim, nodeFocus, view]);
    const focus = useMemo(() => (focusCites ? claimFocus(view, { evidence: focusCites }) : null), [view, focusCites]);
    const focusNode = focusClaim ? `claim:${focusClaim.id}` : nodeFocus;

    const wrapRef = useRef(null);
    const width = useWidth(wrapRef, steps.length > 0);
    const L = useMemo(() => layoutTimeline(view, { width }), [view, width]);
    const spanMs = L.scale ? L.scale.last - L.scale.first : 0;
    const claimNumbers = useMemo(() => new Map(L.badges.map((b) => [b.claimId, b.n])), [L]);

    if (!steps.length) return null;
    const focusLine = focusClaim
        ? `${focusClaim.id} cites ${plural(focusClaim.evidence.length, "anchor")}${focus.events.size ? `, ${plural(focus.events.size, "event")} ringed` : ""}`
        : null;
    const hasChart = view.groups.length || view.machines.length;
    return (
        <div data-graph-timeline>
            {fixed ? (
                <div className="mb-[6px] truncate text-[11px] text-[color:var(--muted)]" data-graph-step-label>
                    {first < step && freshSteps
                        ? `steps ${first + 1} to ${step + 1} of ${steps.length}, ${callText(steps, freshSteps)}`
                        : `step ${step + 1} of ${steps.length}, ${stepText(steps[step])}`}
                </div>
            ) : (
                <StepControl steps={steps} step={step} following={following} onPick={setPicked} tipFor={tipFor} />
            )}
            <div ref={wrapRef} className="w-full">
                {hasChart ? (
                    <GraphChart
                        L={L}
                        view={view}
                        focus={focus}
                        focusNode={focusNode}
                        newWords={newWords}
                        spanMs={spanMs}
                        tipFor={tipFor}
                        onFocusNode={(key) => {
                            if (!key) { setClaimHover(null); setNodeFocus(null); return; }
                            if (key.startsWith("claim:")) setClaimHover(key.slice("claim:".length));
                            else setNodeFocus(key);
                        }}
                    />
                ) : (
                    <div className="py-[4px] text-[11px] text-[color:var(--muted-soft)]">No machines, sessions or events yet.</div>
                )}
                <div className="mt-[4px] min-h-[15px] truncate text-[10.5px] text-[color:var(--muted)]" data-graph-readout aria-live="polite">
                    {focusLine || (hasChart ? "Hover a mark, lane or node to read it; hover a claim to see what it rests on." : "")}
                </div>
                {hasChart ? <Legend newWords={newWords} links={L.combs.length > 0} /> : null}
                {view.people.length ? (
                    <div className="mt-[4px] text-[10px] text-[color:var(--muted)]" data-graph-unplaced="people">
                        also seen, on no machine yet: <span className="text-[color:var(--ink)]">{view.people.map((p) => p.label).join(", ")}</span>
                    </div>
                ) : null}
                {L.unplacedHosts.length ? (
                    <div className="mt-[4px] text-[10px] text-[color:var(--muted)]" data-graph-unplaced="hosts">
                        also reached, by no process on the chart: <span className="text-[color:var(--ink)]">{L.unplacedHosts.map((h) => h.label).join(", ")}</span>
                    </div>
                ) : null}
            </div>
            <ClaimRows claims={view.claims} numbers={claimNumbers} focusId={focusId} pinnedId={claimPin} claimTrace={claimTrace} onOpenTrace={onOpenTrace} onFocus={setClaimHover} onPin={setClaimPin} />
            {tip ? <HoverTooltip anchorRect={tip.rect} placement="top" wide maxWidth={380} label={tip.label} /> : null}
        </div>
    );
}
